import React, { FC, useState } from 'react';
import { ActivityFragmentFragment } from '../../../generated/types';
import Container from '../../Layout/Container';
import { Box, Button, Flex, Heading, VStack } from '@chakra-ui/react';
import { HTMLText } from '../../Common/HTMLText';
import renderTeaser from '../../../utils/render-teaser';
import PageImagesEbikeAtlas, {
  SliderImage,
} from '../../Common/PageImagesEbikeAtlas';
import dynamic from 'next/dynamic';
import useIsMobile from '../../../hooks/useIsMobile';
import { useStore } from '../../../store/provider';
import useActivityHotelActivitiesForCategory from '../../../hooks/useActivityHotelActivitiesForCategory';
import useActivityHotelMarkers from '../../../hooks/useActivityHotelMarkers';
import { ArticleLayout } from '../../Layout/ArticleLayout';

interface ActivityContentProps {
  data: ActivityFragmentFragment;
}

const HotelMap = dynamic(() => import('../../Common/HotelMap'), {
  ssr: false,
});

const ActivityContent: FC<ActivityContentProps> = ({ data }) => {
  const store = useStore();
  const hotel = data.hotels?.items?.length > 0 ? data.hotels.items[0] : null;
  const [showFullText, setShowFullText] = useState(false);

  const images = [
    data.teaserImage,
    ...(data.images ? data.images : []),
  ] as SliderImage[];

  const hotelActivitiesForCategory = useActivityHotelActivitiesForCategory(
    data
  );
  const mapMarkers = useActivityHotelMarkers(data, data.category.key);

  if (data.useArticleLayout) {
    return (
      <ArticleLayout
        paragraphs={data.activityContent as any}
        sidebarParagraphs={data.sidebarBlocks}
      />
    );
  }

  return (
    <Box>
      <Container paddingY={4}>
        <Flex
          flexDirection={{
            base: 'column',
            md: 'column',
            lg: 'row',
          }}
        >
          <Box flex={1}>
            <Heading>{data.title}</Heading>
            {data.body && (
              <Box
                mt={4}
                fontSize={{
                  base: 'md',
                  md: 'lg',
                }}
              >
                <HTMLText text={data?.body} truncate={showFullText ? 0 : 850} />
              </Box>
            )}
            {data.body.length > 850 && !showFullText && (
              <Button mt={4} onClick={() => setShowFullText(true)}>
                Mehr lesen
              </Button>
            )}
          </Box>
          {hotel && (
            <Box
              flex="0 0 auto"
              width={{
                lg: '500px',
              }}
              mt={{
                base: 12,
                md: 0,
              }}
              ml={{
                base: 0,
                md: 12,
              }}
            >
              <Heading mb={8}>{store.config.titles?.activity?.hotel}</Heading>
              {renderTeaser(hotel, {
                vertical: true,
              })}
            </Box>
          )}
        </Flex>
      </Container>

      {data.images?.length > 0 && (
        <Box mt={12}>
          <Container>
            <Heading>Fotos</Heading>

            <PageImagesEbikeAtlas images={images} />
          </Container>
        </Box>
      )}

      <Box mt={12}>
        <HotelMap
          scrollWheelZoomDelayed
          scrollWheelZoom={false}
          data={data}
          markers={mapMarkers}
          center={data.location}
          elevationData={data.elevationData || undefined}
        />
      </Box>

      {store.config.renderConfig.activity.hotelActivities &&
        hotelActivitiesForCategory.category?.length > 0 && (
          <>
            <Container small>
              <Box mt={12}>
                <Heading mb={8}>
                  Weitere {data.category!.labelPlural} des Hotels
                </Heading>
              </Box>
            </Container>
            <Container>
              <Box my={8}>
                <VStack spacing={8}>
                  {hotelActivitiesForCategory.category.map((t) =>
                    renderTeaser(t, {})
                  )}
                </VStack>
              </Box>
            </Container>
          </>
        )}
    </Box>
  );
};

export default ActivityContent;
